import {
  CommunityType,
  GetActivityCommunityDocument,
  GetActivityPostAuthorDocument,
  GetCustomerActivityPostDocument,
  PostType,
} from '~/api/generated/graphql'
import { Link } from 'react-router-dom'
import { elementClicked } from '~/common/EventLogger'
import Counts from '~/pages/posts/Counts'
import MentionableText from '~/common/MentionableText'
import { getPostPath } from '~/utils'
import TimeAgo from '~/common/TimeAgo'
import React, { SyntheticEvent, useEffect, useRef, useState } from 'react'
import { useWindowSize } from '~/common/hooks/useWindowSize'
import { useAuth } from '~/auth/Auth'
import { useQuery } from '@apollo/client'
import { Accordion, useAccordionButton } from 'react-bootstrap'
import { isMacOs } from 'react-device-detect'
import { useClickOnEnter } from '~/common/hooks/useClickOnEnter'
import { useShortcuts } from '~/contexts/ShortcutContext'
import ToastComponent from '~/common/ToastComponent'
import ExpandedActivityRow from '~/pages/feed/ExpandedActivityRow'
import { useCommunity } from '~/contexts/CommunityContext'

type CustomerActivityRowProps = {
  postId: string
  rowExpanded?: (rowId: string, expanded: boolean) => void
}

export const CustomerActivityRow = ({ postId, rowExpanded }: CustomerActivityRowProps) => {
  const { isCondensedPortrait } = useWindowSize()
  const { isVeevan, actingSysAdmin } = useAuth()
  const { companyId, companyName } = useCommunity()
  const eventKey = postId
  const [isExpanded, setIsExpanded] = useState<boolean>(false)

  const { data: postData, loading: loadingPost } = useQuery(GetCustomerActivityPostDocument, {
    variables: { id: postId },
  })
  const post = postData?.post
  const readPost = post?.viewed ?? false

  const { data: postAuthorData, loading: loadingPostAuthor } = useQuery(GetActivityPostAuthorDocument, {
    variables: { id: post?.createdById ?? '' },
    skip: !post?.createdById,
  })

  const lastCustomerCommenter = post?.companyActivityUsers?.[0]

  const loadingAuthors = loadingPostAuthor && !postAuthorData

  const expand = useAccordionButton(eventKey, (e: SyntheticEvent) => {
    setSelectedPost?.({ postId: eventKey, expanded: true })
    elementClicked(e, 'click-post-expanded', { postId })
  })

  const { data: communityData, loading: loadingCommunity } = useQuery(GetActivityCommunityDocument, {
    variables: { id: post?.communityId ?? '' },
    skip: !post?.communityId,
  })
  const community = communityData?.community

  const communityLink = community?.companyId
    ? `/companies/${community?.companyId}/home`
    : `/communities/${post?.communityId}/about`

  const handleClickPostLink = (e: React.MouseEvent<HTMLElement>) => {
    elementClicked(e, 'click-feed-post-link', {
      postId,
    })
    if (isMacOs ? e.metaKey : e.ctrlKey) {
      window.open(getPostPath(community, post))
    } else {
      expand(e)
    }
  }

  const outerDivRef = React.useRef<HTMLDivElement>(null)

  const rowRef = useClickOnEnter<HTMLDivElement>()
  const commRef = useClickOnEnter<HTMLDivElement>()

  const lastActivityTime: Date | undefined = new Date(post?.latestCompanyActivityTime)

  const {
    viewCount = 0,
    veevanViewCount = 0,
    likeCount = 0,
    veevanLikeCount = 0,
    commentCount = 0,
    veevanCommentCount = 0,
  } = post ?? {}

  const [showDeleteToast, setShowDeleteToast] = useState<boolean>(false)
  const { selectedPost, expandCurrentPost, setSelectedPost } = useShortcuts()
  const expandRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isExpanded && selectedPost?.postId === postId && !selectedPost?.expanded) {
      expandRef?.current?.click()
      expandCurrentPost?.()
      outerDivRef?.current?.scrollIntoView({
        behavior: 'auto',
        block: 'center',
        inline: 'center',
      })
    } else if (selectedPost?.postId === postId && !selectedPost?.expanded) {
      expandCurrentPost?.()
      outerDivRef?.current?.scrollIntoView({
        behavior: 'auto',
        block: 'center',
        inline: 'center',
      })
    }
    const rowDiv = rowRef.current
    return () => {
      // prevent previously expanded row from holding onto focus after expanding new row
      rowDiv?.blur()
    }
  }, [selectedPost, postId, expandCurrentPost, isExpanded, outerDivRef, rowRef])

  useEffect(() => {
    if (rowExpanded) {
      rowExpanded(postId, isExpanded)
    }
  }, [postId, isExpanded, rowExpanded])

  if (post?.hidden && !actingSysAdmin)
    return (
      <ToastComponent show={showDeleteToast} onClose={() => setShowDeleteToast(false)} bg={'success'}>
        {'The post was deleted successfully.'}
      </ToastComponent>
    )

  if (loadingAuthors || (loadingCommunity && !communityData) || (loadingPost && !postData)) return null
  const communityInner = (
    <>
      <div
        className="profile-photo community-photo"
        style={community?.photo ? { backgroundImage: `url(${community?.photo})` } : {}}
      />
      <div className={`community-name ${isCondensedPortrait ? 'condensed' : ''}`} ref={commRef}>
        {community?.name}
        {community?.type === CommunityType.Veevan && <div className={'veeva-only-tag'}>Veeva-Only</div>}
      </div>
    </>
  )

  const activityInfo = () => {
    let activityUser = postAuthorData?.user
    const postAuthorInCompany = postAuthorData?.user?.company?.companyId == companyId
    const totalCommentCount = post?.companyActivityUsers?.length ?? 0

    const oneCommenter = totalCommentCount == 1
    const onlyPostAuthor = postAuthorInCompany && totalCommentCount == 0
    const postAuthorCommented = post?.companyActivityUsers?.some(u => u?.userId == postAuthorData?.user?.userId)

    let text = ''
    if (onlyPostAuthor) {
      text = 'posted'
    } else if (postAuthorCommented) {
      if (oneCommenter) {
        text = `posted and commented`
      } else {
        const count = totalCommentCount - 1 // exclude the post author since we name them
        text = `posted and ${count} user${count == 1 ? '' : 's'} commented`
      }
    } else if (postAuthorInCompany) {
      const count = totalCommentCount
      text = `posted and ${count} user${count == 1 ? '' : 's'} commented`
    } else {
      activityUser = lastCustomerCommenter
      if (oneCommenter) {
        text = 'commented'
      } else {
        const count = totalCommentCount - 1 // exclude the latest comment author since we name them
        text = `and ${count} user${count == 1 ? '' : 's'} commented`
      }
    }

    const fullName = `${activityUser?.firstName} ${activityUser?.lastName} (${companyName})`

    const getActivityText = () => {
      return (
        <>
          <div className={`${activityUser?.hidden ? ' inactive' : ''}`}>{fullName}</div>
          &nbsp;
          <div>{text}</div>
          &nbsp;
          <TimeAgo time={new Date(post?.latestCompanyActivityTime)} />
        </>
      )
    }

    return (
      <>
        {isCondensedPortrait ? (
          <div className="activity-description">
            <div
              className={'profile-photo'}
              style={
                activityUser?.photo && !activityUser?.hidden ? { backgroundImage: `url(${activityUser?.photo})` } : {}
              }
            />
            <div className={`activity-author condensed`}>{getActivityText()}</div>
          </div>
        ) : (
          <div className={`activity-description`}>
            <div
              className={'profile-photo'}
              style={
                activityUser?.photo && !activityUser?.hidden ? { backgroundImage: `url(${activityUser?.photo})` } : {}
              }
            />
            <div className={`activity-author`}>{getActivityText()}</div>
          </div>
        )}
      </>
    )
  }

  const lastActivityTimeStamp = () => {
    return (
      <div className={`activity-time${readPost ? ' read' : ' unread'}`}>
        <div>
          <TimeAgo time={lastActivityTime} />
        </div>
      </div>
    )
  }

  return (
    <>
      <div
        ref={outerDivRef}
        className={`feed-row activity-row${isExpanded ? ' expanded' : ''}${post?.hidden ? ' hidden' : ''}${isCondensedPortrait ? ' condensed' : ''}`}
      >
        <div
          className={`activity-item ${isCondensedPortrait ? 'condensed' : ''}`}
          onClick={handleClickPostLink}
          role={'link'}
          ref={expandRef}
        >
          <div className={`activity-post-info ${isCondensedPortrait ? 'condensed' : 'not-condensed'}`}>
            <div
              className={`post-link${isExpanded ? ' expanded' : ''} ${isCondensedPortrait ? 'condensed' : ''}`}
              ref={rowRef}
            >
              <div className={`community-identity ${isCondensedPortrait ? 'condensed' : ''}`}>
                {isExpanded || !isCondensedPortrait ? (
                  <>
                    <Link
                      to={communityLink}
                      className={`community-link ${isCondensedPortrait ? 'condensed' : ''}`}
                      tabIndex={0}
                      onClick={e => {
                        elementClicked(e, 'click-feed-community-link', {
                          postId,
                        })
                        e.stopPropagation()
                      }}
                    >
                      {communityInner}
                    </Link>
                    <div className={'mobile-link-spacer'} />
                  </>
                ) : (
                  communityInner
                )}

                {!post?.hidden && isCondensedPortrait && !isExpanded && (
                  <div className={`activity-menu condensed`}>
                    <Counts
                      isComment={false}
                      likes={likeCount}
                      veevanLikes={veevanLikeCount ?? 0}
                      views={viewCount}
                      veevanViews={veevanViewCount ?? 0}
                      comments={commentCount}
                      veevanComments={veevanCommentCount}
                      isVeevan={isVeevan}
                      communityType={community?.type}
                    />
                  </div>
                )}
              </div>
              {!isExpanded && (
                <div className={`activity-info ${isCondensedPortrait ? 'condensed' : ''}`} tabIndex={0}>
                  <div className={`title-wrapper ${post?.isFollowing ? 'bookmark' : ''}`}>
                    <div className={post?.isFollowing ? 'bookmark' : ''} />
                    <div className={`post-title${readPost ? ' read' : ' unread'} feed-post`}>
                      <MentionableText value={post?.title} postHeader={true} />
                    </div>
                  </div>
                  {activityInfo()}
                </div>
              )}
            </div>
          </div>
          {!isExpanded && (
            <div className={'activity-meta-info'}>
              <div className={'post-stats'}>
                <Counts
                  isComment={false}
                  likes={likeCount}
                  veevanLikes={veevanLikeCount ?? 0}
                  views={viewCount}
                  veevanViews={veevanViewCount ?? 0}
                  comments={commentCount}
                  veevanComments={veevanCommentCount}
                  isVeevan={isVeevan}
                  communityType={community?.type}
                />
              </div>
              {lastActivityTimeStamp()}
            </div>
          )}
        </div>
      </div>
      <Accordion.Collapse
        eventKey={eventKey ?? ''}
        className={`post-expand-zone home-feed ${isCondensedPortrait ? 'condensed' : ''} ${community?.type == CommunityType.Veevan ? ' veeva-only' : ''}`}
        onEnter={() => {
          setIsExpanded(true)
          setSelectedPost?.({ postId, expanded: true })
        }}
        onExited={() => {
          setIsExpanded(false)
          setSelectedPost?.({ postId, expanded: true })
        }}
      >
        <ExpandedActivityRow
          postId={postId}
          postType={post?.postType ?? PostType.Post}
          communityId={post?.communityId ?? ''}
          isExpanded={isExpanded}
          eventKey={eventKey ?? ''}
          onDelete={() => setShowDeleteToast(true)}
          editingPost={false}
          setEditingPost={() => {}}
          isVeevanOnly={false}
        />
      </Accordion.Collapse>
    </>
  )
}
